/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { bool } from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import makeStyles from '@material-ui/core/styles/makeStyles';

import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getFeatureFlag, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getIsBot, getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';
import { getCurrentPageType } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

import MobileGraphqlHeader from './MobileGraphqlHeader';
import GenericSkeleton from '../GraphqlSkeletonComponents/GenericSkeleton';

const useStyles = makeStyles((theme) => ({
    headerWrapper: {
        color: theme.palette.grey?.['50'] || '#aeaeae',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
        padding: '0px 8px',
        width: '100%',
        boxShadow: '0 3px 6px rgba(48,69,98,.16), 0 3px 6px rgba(48,69,98,.23)',
    },
    headerMenuSkeleton: {
        height: '42px',
        width: '30px',
        margin: '8px auto',
    },
    headerLogoSkeleton: {
        height: '42px',
        width: '174px',
        flex: 1,
        margin: '8px auto',
    },
    headerSigninMenuSkeleton: {
        height: '42px',
        width: '30px',
        margin: '8px auto',
    },
    headerCartMenuSkeleton: {
        height: '42px',
        width: '30px',
        margin: '8px auto',
    },
}));

const MobileHeaderContainer = ({ isEyeBrowShow }) => {
    const classes = useStyles();
    const ffWhichCheckoutVariation = useSelector(getFeatureFlag('which-checkout-variation'));
    const ffIsHideMobileHeaderOnScrollEnabled = useSelector(getFeatureFlag('is-hide-mobile-header-on-scroll-enabled'));
    const ffIsHideMobileHeaderOnScrollABTesting = useSelector(getFeatureFlag('is-hide-mobile-header-on-scroll-ab-testing'));
    const ffIsSearchUiInMobileMenuEnabled = useSelector(getFeatureFlag('is-search-ui-in-mobile-menu-enabled'));
    const ffIsSearchUiInMobileMenuABEnabled = useSelector(getFeatureFlag('is-search-ui-in-mobile-menu-ab-testing'));
    const isSearchUiV3Enabled = useSelector(getFeatureFlag('is-search-ui-v3-enabled'));
    const isSearchUiV3ABTestingEnabled = useSelector(getFeatureFlag('is-search-ui-v3-enabled-ab-testing'));

    const brand = useSelector(getBrand);
    const presentationFamily = useSelector(getPresentationFamily);
    const deviceType = useSelector(getSSRDeviceType);
    const pageType = useSelector(getCurrentPageType);
    const isBot = useSelector(getIsBot);

    if (isBot) {
        return (
            <MobileGraphqlHeader
                isEyeBrowShow={isEyeBrowShow}
                brand={brand}
                presentationFamily={presentationFamily}
                ffWhichCheckoutVariation={ffWhichCheckoutVariation}
                ffIsHideMobileHeaderOnScrollEnabled={ffIsHideMobileHeaderOnScrollEnabled}
                isSearchUiInMobileMenuEnabled={ffIsSearchUiInMobileMenuEnabled}
                deviceType={deviceType}
            />
        );
    }

    const {
        error, data, loading, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'MobileHeaderLocation',
        interactionName: `${brand.domain} - Get Campaign - Mobile Search Ui In Menu`,
        featureFlag: ffIsSearchUiInMobileMenuABEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: data?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    const {
        error: searchV3Error, data: searchV3Data, loading: searchV3Loading, variables: searchV3Variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'SearchBarUI',
        interactionName: `${brand.domain} - Get Campaign - Search Bar UI V3`,
        featureFlag: isSearchUiV3ABTestingEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
        apolloOptions: {
            fetchPolicy: 'cache-first',
        },
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: searchV3Data?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    const {
        error: hideOnScrollError, data: hideOnScrollData, loading: hideOnScrollLoading, variables: hideOnScrollVariables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'HideHeaderOnScroll',
        interactionName: `${brand.domain} - Get Campaign - Hide Mobile Header On Scroll`,
        featureFlag: ffIsHideMobileHeaderOnScrollABTesting,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: hideOnScrollData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    if (loading || variables?.skip || hideOnScrollLoading || hideOnScrollVariables?.skip || searchV3Loading || searchV3Variables?.skip) {
        return (
            <div className={classes.headerWrapper}>
                <GenericSkeleton className={classes.headerMenuSkeleton} id="header_ab_testing_skeleton_menu" />
                <GenericSkeleton className={classes.headerMenuSkeleton} id="header_ab_testing_skeleton_search" />
                <GenericSkeleton className={classes.headerLogoSkeleton} id="header_ab_testing_skeleton_logo" />
                <GenericSkeleton className={classes.headerCartMenuSkeleton} id="header_ab_testing_skeleton_cart" />
                <GenericSkeleton className={classes.headerSigninMenuSkeleton} id="header_ab_testing_skeleton_sign_in" />
            </div>
        );
    }

    if (hideOnScrollError) {
        mbpLogger.logError({
            function: 'MobileHeaderContainer',
            message: 'Error loading API data for HideHeaderOnScroll',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    if (searchV3Error) {
        mbpLogger.logError({
            function: 'MobileHeaderContainer',
            message: 'Error loading API data for SearchBarUI',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: searchV3Error,
        });
    }

    const hideOnScrollUserGroup = hideOnScrollData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const isMobileHeaderHideOnScroll = hideOnScrollUserGroup?.toLowerCase?.() === 'test';
    const searchV3UserGroup = searchV3Data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    if (error) {
        mbpLogger.logError({
            function: 'MobileHeaderContainer',
            message: 'Error loading API data for MobileHeaderLocation',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
        return (
            <MobileGraphqlHeader
                isEyeBrowShow={isEyeBrowShow}
                brand={brand}
                presentationFamily={presentationFamily}
                ffWhichCheckoutVariation={ffWhichCheckoutVariation}
                ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll || ffIsHideMobileHeaderOnScrollEnabled}
                isSearchUiInMobileMenuEnabled={ffIsSearchUiInMobileMenuEnabled}
                deviceType={deviceType}
                mobileHideHeaderOnScrollResponse={hideOnScrollData?.contentSF?.campaign?.campaignResponses?.[0] || {}}
                isSearchUiV3Enabled={searchV3UserGroup?.toLowerCase?.() === 'test' || isSearchUiV3Enabled}
                searchUiV3SalesforceResponse={searchV3Data?.contentSF || {}}
            />
        );
    }

    const userGroup = data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const isSearchUiInMobileMenuEnabled = userGroup?.toLowerCase?.() === 'test';

    return (
        <MobileGraphqlHeader
            isEyeBrowShow={isEyeBrowShow}
            brand={brand}
            presentationFamily={presentationFamily}
            ffWhichCheckoutVariation={ffWhichCheckoutVariation}
            ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll || ffIsHideMobileHeaderOnScrollEnabled}
            isSearchUiInMobileMenuEnabled={isSearchUiInMobileMenuEnabled || ffIsSearchUiInMobileMenuEnabled}
            mobileMenuCampaignResponse={data?.contentSF?.campaign?.campaignResponses?.[0]}
            deviceType={deviceType}
            mobileHideHeaderOnScrollResponse={hideOnScrollData?.contentSF?.campaign?.campaignResponses?.[0] || {}}
            isSearchUiV3Enabled={searchV3UserGroup?.toLowerCase?.() === 'test' || isSearchUiV3Enabled}
            searchUiV3SalesforceResponse={searchV3Data?.contentSF || {}}
        />
    );
};

MobileHeaderContainer.propTypes = {
    isEyeBrowShow: bool.isRequired,
};

export default MobileHeaderContainer;
